import { useEffect, useMemo, useState } from "react";
import { FiClock, FiMinus, FiPlus, FiSave, FiX } from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const NO_TIME_STATUSES = ["ABSENT", "ON_LEAVE", "WEEKLY_OFF", "HOLIDAY"];

const STATUS_OPTIONS = [
  ["PRESENT", "Present"],
  ["LATE", "Late"],
  ["HALF_DAY", "Half day"],
  ["ABSENT", "Absent"],
  ["ON_LEAVE", "On leave"],
  ["WEEKLY_OFF", "Weekly off"],
  ["HOLIDAY", "Holiday"],
];

function getIndiaTime(dateValue) {
  if (!dateValue) return "";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(dateValue));

  const hour = parts.find((part) => part.type === "hour")?.value || "00";
  const minute = parts.find((part) => part.type === "minute")?.value || "00";

  return `${hour}:${minute}`;
}

function formatIndiaTime(dateValue) {
  if (!dateValue) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(dateValue));
}

function formatMinutes(minutes) {
  const value = Number(minutes || 0);

  if (!value) return "0 min";

  const hours = Math.floor(value / 60);
  const remainingMinutes = value % 60;

  if (!hours) return `${remainingMinutes} min`;
  if (!remainingMinutes) return `${hours}h`;

  return `${hours}h ${remainingMinutes}m`;
}

function toIndiaIso(date, time) {
  return `${date}T${time}:00+05:30`;
}

function toPunches(record) {
  if (!record?.sessions?.length) {
    return [{ checkIn: "", checkOut: "" }];
  }

  return record.sessions.map((session) => ({
    checkIn: getIndiaTime(session.checkInAt),
    checkOut: getIndiaTime(session.checkOutAt),
  }));
}

export default function AttendanceDayEditorModal({
  open,
  onClose,
  employee,
  selectedDay,
  onSaved,
}) {
  const [status, setStatus] = useState("ABSENT");
  const [punches, setPunches] = useState([{ checkIn: "", checkOut: "" }]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !selectedDay) return;

    setStatus(selectedDay.record?.status || "ABSENT");
    setPunches(toPunches(selectedDay.record));
    setReason("");
    setError("");
  }, [open, selectedDay]);

  const isWorkingDay = !NO_TIME_STATUSES.includes(status);

  const workedMinutes = useMemo(() => {
    return punches.reduce((total, punch) => {
      if (!punch.checkIn || !punch.checkOut) return total;

      const [inHour, inMinute] = punch.checkIn.split(":").map(Number);
      const [outHour, outMinute] = punch.checkOut.split(":").map(Number);

      const checkInMinutes = inHour * 60 + inMinute;
      const checkOutMinutes = outHour * 60 + outMinute;

      return total + Math.max(0, checkOutMinutes - checkInMinutes);
    }, 0);
  }, [punches]);

  if (!open || !selectedDay) return null;

  const schedule = employee?.workSchedule;
  const shiftText =
    schedule?.shiftStartTime && schedule?.shiftEndTime
      ? `${schedule.shiftStartTime} – ${schedule.shiftEndTime}`
      : "Shift not configured";

  const updatePunch = (index, field, value) => {
    setPunches((current) =>
      current.map((punch, punchIndex) =>
        punchIndex === index ? { ...punch, [field]: value } : punch,
      ),
    );
  };

  const addPunch = () => {
    setPunches((current) => [...current, { checkIn: "", checkOut: "" }]);
  };

  const removePunch = (index) => {
    setPunches((current) => {
      if (current.length === 1) return current;
      return current.filter((_, punchIndex) => punchIndex !== index);
    });
  };

  const handleStatusChange = (nextStatus) => {
    setStatus(nextStatus);

    if (NO_TIME_STATUSES.includes(nextStatus)) {
      setPunches([]);
    } else if (!punches.length) {
      setPunches([{ checkIn: "", checkOut: "" }]);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");

    if (!reason.trim()) {
      setError("Add an internal remark explaining this attendance update.");
      return;
    }

    if (isWorkingDay) {
      if (!punches.length) {
        setError("Add at least one IN and OUT time punch.");
        return;
      }

      const hasIncompletePunch = punches.some(
        (punch) => !punch.checkIn || !punch.checkOut,
      );

      if (hasIncompletePunch) {
        setError("Every time punch requires both an IN and OUT time.");
        return;
      }

      const hasInvalidPunch = punches.some(
        (punch) => punch.checkOut <= punch.checkIn,
      );

      if (hasInvalidPunch) {
        setError("Each OUT time must be later than its IN time.");
        return;
      }
    }

    setIsSaving(true);

    try {
      await apiRequest("/attendance/manual", {
        method: "POST",
        accessToken: employee.accessToken,
        body: {
          employeeId: employee.id,
          attendanceDate: selectedDay.date,
          status,
          sessions: isWorkingDay
            ? punches.map((punch) => ({
                checkInAt: toIndiaIso(selectedDay.date, punch.checkIn),
                checkOutAt: toIndiaIso(selectedDay.date, punch.checkOut),
                note: reason.trim(),
              }))
            : [],
          reason: reason.trim(),
        },
      });

      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Unable to save attendance.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto bg-slate-950/55 p-3 sm:p-6">
      <div className="mx-auto flex min-h-full max-w-6xl items-center">
        <div className="w-full overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
            <div>
              <p className="text-sm text-gray-500">Attendance management</p>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">
                Update Attendance
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              <FiX size={22} />
            </button>
          </div>

          <form onSubmit={handleSave}>
            <div className="grid gap-8 p-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Attendance date
                    </p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {new Intl.DateTimeFormat("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }).format(new Date(`${selectedDay.date}T00:00:00+05:30`))}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Employee
                    </p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {employee?.fullName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {employee?.employeeCode || "—"}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Assigned shift
                  </p>
                  <p className="mt-1 font-semibold text-gray-900">
                    {shiftText}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Grace: {schedule?.graceMinutes || 0} min · Break:{" "}
                    {schedule?.breakDurationMinutes || 0} min
                  </p>
                </div>

                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-700">
                    Attendance status
                  </span>
                  <select
                    value={status}
                    onChange={(event) => handleStatusChange(event.target.value)}
                    className="h-12 w-full rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                  >
                    {STATUS_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
                  Saving creates an auditable admin attendance adjustment.
                  Existing time punches for this day are replaced by the punches
                  below.
                </div>

                <label className="block">
                  <span className="mb-2 block text-sm font-semibold text-gray-700">
                    Internal remarks
                  </span>
                  <textarea
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Example: Face machine was offline; attendance corrected after manager review."
                    rows={4}
                    maxLength={1000}
                    className="w-full resize-none rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Visible to HR and administrators only.
                  </p>
                </label>
              </div>

              <div className="space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Time Punches
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Add every IN and OUT session for this workday.
                    </p>
                  </div>

                  {isWorkingDay && (
                    <button
                      type="button"
                      onClick={addPunch}
                      className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white hover:bg-primary-900"
                    >
                      <FiPlus />
                      Add Punch
                    </button>
                  )}
                </div>

                {!isWorkingDay ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
                    {status.replaceAll("_", " ")} does not require time punches.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {punches.map((punch, index) => (
                      <div
                        key={`punch-${index}`}
                        className="rounded-2xl border border-gray-200 bg-white p-4"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-xs font-bold text-primary-800">
                              {index + 1}
                            </span>
                            <p className="font-semibold text-gray-800">
                              Work session
                            </p>
                          </div>

                          <button
                            type="button"
                            disabled={punches.length === 1}
                            onClick={() => removePunch(index)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-30"
                          >
                            <FiMinus />
                          </button>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <label>
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                              IN
                            </span>
                            <input
                              type="time"
                              value={punch.checkIn}
                              onChange={(event) =>
                                updatePunch(
                                  index,
                                  "checkIn",
                                  event.target.value,
                                )
                              }
                              className="h-11 w-full rounded-xl border border-gray-200 px-3 outline-none focus:border-primary-700"
                            />
                          </label>

                          <label>
                            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                              OUT
                            </span>
                            <input
                              type="time"
                              value={punch.checkOut}
                              onChange={(event) =>
                                updatePunch(
                                  index,
                                  "checkOut",
                                  event.target.value,
                                )
                              }
                              className="h-11 w-full rounded-xl border border-gray-200 px-3 outline-none focus:border-primary-700"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Total worked
                    </p>
                    <p className="mt-2 text-xl font-bold text-gray-900">
                      {formatMinutes(workedMinutes)}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Previous status
                    </p>
                    <p className="mt-2 text-xl font-bold text-gray-900">
                      {selectedDay.record?.status?.replaceAll("_", " ") ||
                        "No record"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Existing punches
                    </p>
                    <p className="mt-2 text-xl font-bold text-gray-900">
                      {selectedDay.record?.sessions?.length || 0}
                    </p>
                  </div>
                </div>

                {selectedDay.record?.sessions?.length ? (
                  <div className="rounded-2xl border border-gray-100 p-4">
                    <p className="mb-3 text-sm font-semibold text-gray-800">
                      Existing saved punches
                    </p>

                    <div className="space-y-2">
                      {selectedDay.record.sessions.map((session, index) => (
                        <div
                          key={session.id || index}
                          className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 text-sm"
                        >
                          <span className="inline-flex items-center gap-2 text-gray-600">
                            <FiClock className="text-primary-700" />
                            Session {index + 1}
                          </span>
                          <span className="font-semibold text-gray-900">
                            {formatIndiaTime(session.checkInAt)} –{" "}
                            {formatIndiaTime(session.checkOutAt)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {error && (
              <div className="mx-6 mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 px-6 py-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="h-11 rounded-xl border border-gray-200 px-5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 font-semibold text-white hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiSave />
                {isSaving ? "Saving..." : "Save Attendance"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
